import { CopyIcon, ViewIcon } from '@chakra-ui/icons'
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu'
import {
  Center,
  Grid,
  GridItem,
  IconButton,
  Input,
  Text,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react'
import { Td, Tr } from '@chakra-ui/table'
import moment from 'moment'
import React, { useContext } from 'react'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import type { DeveloperKey } from '../../interfaces'
import useToast from '../../hooks/useToast'
import PermissionGuard from '../PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import { UserContext } from '../../providers'
import CreateUpdateRestrictedApiKeyModal from './CreateUpdateRestrictedApiKeyModal'

const PublicKeyTd = ({
  publicKey,
  onCopyPublicKey,
}: {
  publicKey: string
  onCopyPublicKey(): void
}): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  return (
    <Td>
      <Grid templateColumns="auto auto">
        <GridItem>
          <Center>
            <Input
              paddingX={2}
              marginX={2}
              minWidth={150}
              contentEditable={false}
              fontFamily="monospace"
              value={publicKey}
            />
          </Center>
        </GridItem>
        <GridItem>
          <Center
            marginTop={3}
            cursor="pointer"
            onClick={() => {
              onCopyPublicKey()
              toast({
                description: t('copied', 'Copied'),
                status: 'success',
                position: 'top-right',
                isClosable: true,
                duration: 5000,
              })
            }}
          >
            <CopyIcon />
          </Center>
        </GridItem>
      </Grid>
    </Td>
  )
}

const PrivateKeyTd = ({
  privateKey,
  revealed,
  canReveal,
  onCopyPrivateKey,
  onOpenRevealApiKey,
}: {
  privateKey: string
  revealed: boolean
  canReveal: boolean
  onCopyPrivateKey(): void
  onOpenRevealApiKey(): void
}): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const privateKeyDisplay = revealed
    ? privateKey
    : `${privateKey.substring(0, 12)}${privateKey
        .substring(12)
        .replace(/./g, '*')}` // If private key is not revealed, display it as `private_1234*************`

  return (
    <Td>
      <Grid templateColumns={canReveal ? 'auto auto' : 'auto'}>
        <GridItem>
          <Center>
            <Input
              paddingX={2}
              marginX={2}
              minWidth={150}
              contentEditable={false}
              fontFamily="monospace"
              value={privateKeyDisplay}
            />
          </Center>
        </GridItem>
        {canReveal ? (
          <GridItem>
            {revealed ? (
              <Center
                marginTop={3}
                cursor="pointer"
                onClick={() => {
                  onCopyPrivateKey()
                  toast({
                    description: t('copied', 'Copied'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                    duration: 5000,
                  })
                }}
              >
                <CopyIcon />
              </Center>
            ) : (
              <Center cursor="pointer">
                <ViewIcon
                  marginTop={3}
                  cursor="pointer"
                  onClick={onOpenRevealApiKey}
                />
              </Center>
            )}
          </GridItem>
        ) : null}
      </Grid>
    </Td>
  )
}

const MenuTd = ({
  apiKey,
  apiKeyId,
  apiKeyEnabled,
  keyType,
  setSelectedKeyId,
  onRollApiKey,
  onDeleteApiKey,
  getData,
}: {
  apiKeyId: string
  apiKeyEnabled: boolean
  keyType: 'standard' | 'restricted' | undefined
  apiKey: DeveloperKey
  setSelectedKeyId(id: string): void
  onRollApiKey(): void
  onDeleteApiKey(): void
  getData(): Promise<void>
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const {
    isOpen: isOpenCreateRestrictedApiKey,
    onOpen: onOpenCreateRestrictedApiKey,
    onClose: onCloseCreateRestrictedApiKey,
  } = useDisclosure()

  return (
    <>
      <Td>
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<BsThreeDotsVertical />}
            variant="link"
            boxShadow="none"
          />
          {keyType !== 'restricted' ? (
            <MenuList>
              {apiKeyEnabled ? (
                <MenuItem
                  onClick={() => {
                    setSelectedKeyId(apiKeyId)
                    onRollApiKey()
                  }}
                >
                  {t('roll_keys', 'Roll Keys')}
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() => {
                    setSelectedKeyId(apiKeyId)
                    onDeleteApiKey()
                  }}
                  color="appBrick"
                >
                  {t('delete_keys', 'Delete Keys')}
                </MenuItem>
              )}
            </MenuList>
          ) : (
            <MenuList>
              {apiKeyEnabled ? (
                <>
                  <MenuItem
                    onClick={() => {
                      setSelectedKeyId(apiKeyId)
                      onOpenCreateRestrictedApiKey()
                    }}
                  >
                    {t('view_update_permissions', 'View/update permissions')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedKeyId(apiKeyId)
                      onRollApiKey()
                    }}
                  >
                    {t('roll_keys', 'Roll Keys')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedKeyId(apiKeyId)
                      onDeleteApiKey()
                    }}
                    color="appBrick"
                  >
                    {t('delete_keys', 'Delete Keys')}
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={() => {
                      setSelectedKeyId(apiKeyId)
                      onOpenCreateRestrictedApiKey()
                    }}
                  >
                    {t('view_update_permissions', 'View/update permissions')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedKeyId(apiKeyId)
                      onDeleteApiKey()
                    }}
                    color="appBrick"
                  >
                    {t('delete_keys', 'Delete Keys')}
                  </MenuItem>
                </>
              )}
            </MenuList>
          )}
        </Menu>
      </Td>
      <CreateUpdateRestrictedApiKeyModal
        isCreate={false}
        apiKey={apiKey}
        user={user}
        isOpen={isOpenCreateRestrictedApiKey}
        apiKeyId={apiKeyId}
        onClose={onCloseCreateRestrictedApiKey}
        getData={getData}
      />
    </>
  )
}

interface Props {
  id: string
  apiKey: DeveloperKey
  setSelectedKeyId: React.Dispatch<React.SetStateAction<string | null>>
  onOpenRevealApiKey(): void
  onRollApiKey(): void
  onDeleteApiKey(): void
  getData(): Promise<void>
}

const ApiKeyRow = ({
  id,
  apiKey,
  setSelectedKeyId,
  onOpenRevealApiKey,
  onRollApiKey,
  onDeleteApiKey,
  getData,
}: Props): React.ReactElement => {
  const { onCopy: onCopyPublicKey } = useClipboard(apiKey.publicKey)
  const { onCopy: onCopyPrivateKey } = useClipboard(apiKey.privateKey)

  return (
    <PermissionGuard permission={PermissionsEnum.SELF_DEVELOPERS_MANAGE}>
      {(hasPermission: boolean) => (
        <Tr key={id}>
          <PublicKeyTd
            publicKey={apiKey.publicKey}
            onCopyPublicKey={onCopyPublicKey}
          />
          <PrivateKeyTd
            privateKey={apiKey.privateKey}
            revealed={apiKey.revealed}
            canReveal={hasPermission}
            onCopyPrivateKey={onCopyPrivateKey}
            onOpenRevealApiKey={onOpenRevealApiKey}
          />
          <Td>
            <Text width={100}>
              {moment(apiKey.createdAt).format('MMMM Do YYYY')}
            </Text>
          </Td>
          <Td
            paddingTop="0px"
            paddingBottom="0px"
            fontSize="1em"
            color={
              apiKey.enabled ? colors.appClearBlue : colors.appYellowOrange
            }
          >
            {apiKey.enabled ? 'Enabled' : `Expires ${apiKey.expirationTime}`}
          </Td>
          <MenuTd
            apiKeyId={id}
            apiKeyEnabled={apiKey.enabled}
            setSelectedKeyId={setSelectedKeyId}
            onRollApiKey={onRollApiKey}
            onDeleteApiKey={onDeleteApiKey}
            keyType={apiKey.type}
            apiKey={apiKey}
            getData={getData}
          />
        </Tr>
      )}
    </PermissionGuard>
  )
}

export default ApiKeyRow
